import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  getFirestore,
  writeBatch,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  updateDoc,
  doc,
  orderBy,
  deleteDoc,
  setDoc,
  type DocumentReference,
} from "firebase/firestore";
import { getAuth, signInWithPhoneNumber, signOut, RecaptchaVerifier } from "firebase/auth";
import {
  logError,
  signOutAuth,
  updateReqData,
  signInUpdateAuth,
  updateProducts,
  updateRestaurants,
  updateSelectedRestaurant,
  updateGlobalConfig,
  updatePublicRestaurants,
} from "@actions/userActions";
import type { AppDispatch } from "@app/store";
import { getNextOrderSeqNo } from "@src/utilities/orderSequence";
import { serializeForRedux } from "@src/utilities/serializeForRedux";

const restaurantsCollectionName = "restaurants";
const roomBillsCollectionName = "room-bills";

/** History-like object (e.g. from react-router) */
export interface HistoryLike {
  push: (path: string) => void;
}

/** Redirect function */
export type RedirectFn = (path: string) => void;

/** Log error payload */
interface LogErrorPayload {
  severity?: string;
  message?: string;
}

/** Roles map: docId -> role or similar */
export type RolesMap = Record<string, unknown>;

export type GetRestaurantsOptions = {
  /** Skip the global success snackbar (e.g. background refresh on page load). */
  silent?: boolean;
  /** Keep this restaurant selected after sync; falls back to localStorage then first result. */
  preserveSelectedRestId?: string;
};

type RestaurantRecord = Record<string, unknown> & { docId?: string };

function resolveSelectedRestaurant(
  results: RestaurantRecord[],
  preserveSelectedRestId?: string
): RestaurantRecord {
  let currentId = preserveSelectedRestId;
  if (!currentId && typeof localStorage !== "undefined") {
    try {
      const stored = JSON.parse(localStorage.getItem("SELECTED_RESTAURANT") || "null") as {
        docId?: string;
      } | null;
      currentId = stored?.docId;
    } catch {
      /* ignore invalid localStorage */
    }
  }
  if (currentId) {
    const match = results.find((r) => r.docId === currentId);
    if (match) return match;
  }
  return results[0];
}

/** Room bill / order data */
export interface RoomBillData {
  orderId?: string;
  [key: string]: unknown;
}

export const initializeFirebase = (): void => {
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIRE_apiKey,
    authDomain: process.env.REACT_APP_FIRE_authDomain,
    projectId: process.env.REACT_APP_FIRE_projectId,
    storageBucket: process.env.REACT_APP_FIRE_storageBucket,
    messagingSenderId: process.env.REACT_APP_FIRE_messagingSenderId,
    appId: process.env.REACT_APP_FIRE_appId,
    measurementId: process.env.REACT_APP_FIRE_measurementId,
  };

  const app = initializeApp(firebaseConfig);
  getAnalytics(app);
};

export const signInWithPhone = (
  phone: string,
  _redirect: RedirectFn,
  _dispatch: AppDispatch
): void => {
  const auth = getAuth();
  auth.languageCode = "it";
  const appVerifier = new RecaptchaVerifier("recaptcha-container", {}, auth);

  signInWithPhoneNumber(auth, phone, appVerifier)
    .then((confirmationResult) => {
      const verificationCode = window.prompt("Please enter the verification code:", "");
      confirmationResult
        .confirm(verificationCode ?? "")
        .then(() => {})
        .catch((error: { code?: string; message?: string }) => {
          console.log(error.code, error.message);
        });
    })
    .catch((error: { code?: string; message?: string }) => {
      console.log(error.code, error.message);
    });
};

export const signOutMain = (
  history: HistoryLike,
  dispatch: AppDispatch
): void => {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("userData", String(null));
  }
  const auth = getAuth();
  signOut(auth)
    .then(() => {
      dispatch(signOutAuth({}));
      dispatch(updateReqData([]));
      history.push("/sign-in");
    })
    .catch((error: unknown) => {
      console.log("error----", error);
    });
};

export const getUserData = async (
  userId: string | undefined,
  redirect: RedirectFn,
  dispatch: AppDispatch
): Promise<void> => {
  const db = getFirestore();
  try {
    let data: Record<string, unknown> = {};
    if (userId) {
      const q = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((snap) => {
        data = snap.data() as Record<string, unknown>;
      });

      if (data && data.uid) {
        dispatch(
          signInUpdateAuth(
            serializeForRedux(data) as Parameters<typeof signInUpdateAuth>[0]
          )
        );
      }
      redirect("/");
    }
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

export const uploadData = async (
  data: Record<string, unknown>,
  collection_name: string
): Promise<void> => {
  const db = getFirestore();
  try {
    if (collection_name) {
      const docRef = await addDoc(collection(db, collection_name), {
        ...data,
      });
      console.log("Document written with ID: ", docRef.id);
    }
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

export const uploadBatchData = async (
  data: Record<string, unknown>[],
  collection_name: string
): Promise<void> => {
  const db = getFirestore();
  const batch = writeBatch(db);
  const collectionRef = collection(db, collection_name);

  data.forEach((d) => {
    const cRef = doc(collectionRef);
    batch.set(cRef, d);
  });

  try {
    await batch.commit();
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

export const getRestaurantDetails = async (
  restaurantId: string,
  dispatch: AppDispatch
): Promise<void> => {
  const db = getFirestore();
  try {
    let data: Record<string, unknown> = {};
    const snap = await getDoc(doc(db, restaurantsCollectionName, restaurantId));

    if (snap.exists()) {
      data = snap.data() as Record<string, unknown>;
    } else {
      console.log("No such document");
    }
    if (data && Object.keys(data).length) {
      dispatch(updateProducts(data));
    }
  } catch (error: unknown) {
    console.error("Error adding document: ", error);
    dispatch(
      logError({
        severity: "error",
        message: error instanceof Error ? error.message : String(error),
      } as LogErrorPayload)
    );
  }
};

export const getRestaurants = async (
  dispatch: AppDispatch,
  roles: RolesMap | undefined,
  options?: GetRestaurantsOptions
): Promise<void> => {
  const db = getFirestore();
  try {
    let data: Array<Record<string, unknown> & { docId?: string }> = [];
    if (roles && Object.keys(roles).length) {
      const promises = Object.keys(roles).map((docId) =>
        getDoc(doc(db, restaurantsCollectionName, docId))
      );
      try {
        const snapshots = await Promise.all(promises);
        const rawResults = snapshots
          .map((snapshot) => {
            if (snapshot.exists()) {
              return { docId: snapshot.id, ...snapshot.data() };
            }
            return null;
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);

        const results = serializeForRedux(rawResults);

        if (results && results.length) {
          const selected = resolveSelectedRestaurant(results, options?.preserveSelectedRestId);
          dispatch(updateRestaurants(results));
          dispatch(updateSelectedRestaurant(selected));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("RESTAURANTS_DATA", JSON.stringify(results));
            localStorage.setItem("SELECTED_RESTAURANT", JSON.stringify(selected));
          }
        }
        if (!options?.silent) {
          dispatch(
            logError({ severity: "success", message: "Restaurant Data Updated!" } as LogErrorPayload)
          );
        }
      } catch (error: unknown) {
        console.error("Error fetching documents:", error);
        dispatch(
          logError({
            severity: "error",
            message: error instanceof Error ? error.message : String(error),
          } as LogErrorPayload)
        );
        throw error;
      }
    } else {
      const q = query(
        collection(db, restaurantsCollectionName),
        orderBy("name")
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((snap) => {
        data.push({ ...snap.data(), docId: snap.id });
      });
      const serializedData = serializeForRedux(data);
      if (serializedData && serializedData.length) {
        dispatch(updatePublicRestaurants(serializedData));
        if (typeof localStorage !== "undefined") {
          localStorage.setItem("PUBLIC_RESTAURANTS_DATA", JSON.stringify(serializedData));
        }
      }
    }
    if (typeof localStorage !== "undefined" && process.env.REACT_APP_VERSION) {
      localStorage.setItem("REACT_APP_VERSION", process.env.REACT_APP_VERSION);
    }
  } catch (error: unknown) {
    console.error("Error adding document: ", error);
    dispatch(
      logError({
        severity: "error",
        message: error instanceof Error ? error.message : String(error),
      } as LogErrorPayload)
    );
  }
};

export const updateCollection = async (
  collectionName: string,
  docId: string,
  key: string,
  value: unknown
): Promise<boolean> => {
  const db = getFirestore();
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await updateDoc(doc(db, collectionName, docId), { [key]: value } as any);
    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
};

/** Firestore rejects `undefined` field values; strip them before write. */
const omitUndefinedFields = <T extends Record<string, unknown>>(obj: T): T => {
  const out = { ...obj };
  (Object.keys(out) as (keyof T)[]).forEach((key) => {
    if (out[key] === undefined) {
      delete out[key];
    }
  });
  return out;
};

export const createRoomBills = async (
  data: RoomBillData | null | undefined,
  dispatch: AppDispatch,
  _navigate?: ((path: string) => void) | null,
  _restaurantId?: string
): Promise<DocumentReference | boolean | void> => {
  const db = getFirestore();
  try {
    if (data?.orderId) {
      // Firestore UpdateData expects FieldValue; we pass dynamic payload
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateDoc(
        doc(db, roomBillsCollectionName, data.orderId),
        omitUndefinedFields({ ...data } as Record<string, unknown>) as any
      );
      return true;
    }
    if (data) {
      const restaurantId =
        (typeof data.restaurantId === 'string' ? data.restaurantId : undefined) ||
        _restaurantId;
      if (!restaurantId) {
        throw new Error('restaurantId is required to create a room bill');
      }
      const isGstBill = !!data.isGstBill;
      const seqFields = isGstBill
        ? await getNextOrderSeqNo(
            restaurantId,
            'RM',
            typeof data.createdAt === 'number' ? data.createdAt : undefined
          )
        : {};
      // Firestore addDoc data type is strict; we pass dynamic payload
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const docRef = await addDoc(
        collection(db, roomBillsCollectionName),
        omitUndefinedFields({
          ...data,
          ...seqFields,
        } as Record<string, unknown>) as any
      );
      console.log("Order written with ID: ", docRef.id);
      if (docRef.id) {
        return docRef;
      }
    }
  } catch (error: unknown) {
    console.error("Error adding document: ", error);
    dispatch(
      logError({
        severity: "error",
        message: error instanceof Error ? error.message : String(error),
      } as LogErrorPayload)
    );
    if (data?.orderId) {
      return false;
    }
  }
};

export const createCollection = async (
  connectionName: string,
  data: Record<string, unknown>
): Promise<DocumentReference | undefined> => {
  const db = getFirestore();
  try {
    console.log("creating new -> ", connectionName);
    const docRef = await addDoc(collection(db, connectionName), {
      ...data,
    });
    console.log(`Created ${connectionName} with ID: `, docRef.id);
    if (docRef.id) {
      return docRef;
    }
  } catch (error: unknown) {
    console.error("Error adding document: ", error);
  }
  return undefined;
};

export const removeRoomBills = async (
  orderId: string | undefined
): Promise<boolean> => {
  const db = getFirestore();
  try {
    if (!orderId) return false;
    console.log("deleting order", orderId);
    await deleteDoc(doc(db, roomBillsCollectionName, orderId));
    return true;
  } catch (error: unknown) {
    console.error("Error deleting room bill: ", error);
    return false;
  }
};

export const deleteCollection = async (
  tCollectionName: string,
  orderId: string | undefined
): Promise<boolean> => {
  const db = getFirestore();
  try {
    if (!orderId || !tCollectionName) return false;
    console.log("deleting order", orderId);
    await deleteDoc(doc(db, tCollectionName, orderId));
    return true;
  } catch (error: unknown) {
    console.error("Error deleting document: ", error);
    return false;
  }
};

export const getGlobalConfig = async (dispatch: AppDispatch): Promise<void> => {
  try {
    const db = getFirestore();
    const data: Record<string, unknown> = {};
    const q = query(collection(db, "global-config"));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((snap) => {
      data[snap?.id ?? ""] = snap.data();
    });
    if (Object.keys(data).length) {
      dispatch(updateGlobalConfig(data));
    }
  } catch (error: unknown) {
    console.error("Error getting global config: ", error);
    dispatch(
      logError({
        severity: "error",
        message: error instanceof Error ? error.message : String(error),
      } as LogErrorPayload)
    );
  }
};

export const createUserData = async (
  userId: string | undefined,
  userData: Record<string, unknown>
): Promise<void> => {
  const db = getFirestore();
  if (!userId) {
    console.error("User data must include a unique 'id' field.");
    return;
  }

  try {
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      console.log("User already exists. Updating data...");
      await setDoc(userDocRef, userData, { merge: true });
      console.log("User document updated with ID:", userId);
    } else {
      console.log("User does not exist. Creating new document...");
      await setDoc(userDocRef, userData);
      console.log("User document created with ID:", userId);
    }
  } catch (error: unknown) {
    console.error("Error creating user document:", error);
  }
};
